import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { FaroText, grey } from "@faro-lotv/flat-ui";
import { NO_TRANSLATE_CLASS } from "@faro-lotv/foreign-observers";
import type { AiCocoRleMask, AiMaskFile } from "@faro-lotv/service-wires";
import { useLotvDispose } from "@faro-lotv/spatial-ui";
import { alpha } from "@mui/system";
import { useCursor } from "@react-three/drei";
import { useThree, type ThreeEvent } from "@react-three/fiber";
import { useEffect, useMemo, useState, type JSX, type Ref } from "react";
import {
  DataTexture,
  DoubleSide,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  RGFormat,
  type Vector2,
  type Vector3,
} from "three";
import { maskBoxCoordinates } from "./ai-mask-file";
import { boundingBoxToSphereGeometry } from "./bounding-box-geometry";
import { rasterizeRleCrop, type MaskCrop } from "./coco-rle";

/** Kept low so the panorama stays readable, like the regions' fill. */
const FILL_OPACITY = 0.35;

/** The opacity of the mask under the pointer. */
const HOVERED_OPACITY = 0.7;

/**
 * The maximum width and height of a mask's texture. At the panorama's own resolution, the masks of one panorama took
 * up to 200 MB of GPU memory (large surfaces span the whole panorama); capped, at most 37 MB.
 */
const MAX_TEXTURE_SIZE = 4096;

/** How far the pointer may move between press and release, in pixels, for a click rather than a drag. */
const CLICK_TOLERANCE = 4;

/** The rasterized masks, so a finding shown again doesn't decode its mask again. */
const crops = new WeakMap<AiCocoRleMask, MaskCrop>();

export type BoundingMaskMeshProps = {
  /** The mask of the finding. */
  mask: AiCocoRleMask;

  /** The size of the image the mask was computed on. */
  imageSize: AiMaskFile["imageSize"];

  /** The color of the finding. */
  color: string;

  /**
   * The render order of the mask. All findings lie on the same sphere, so overlapping masks need distinct ones, or
   * their drawing order flips with the camera.
   */
  renderOrder: number;

  /** Called when the mask is clicked, where it covers the image. */
  onClick?(): void;

  /** Shown next to the cursor while it is on the mask, e.g. the finding's label when no box label is shown. */
  tooltip?: string;

  /** Optional ref to the mesh, e.g. to capture a screenshot of it. */
  meshRef?: Ref<Mesh>;
};

/**
 * @returns the mask of a single AI finding, drawn as a texture on the part of the unit sphere its box covers. It is
 * highlighted, and can be clicked, only where the mask covers the image, not on its whole box.
 */
export function BoundingMaskMesh({
  mask,
  imageSize,
  color,
  renderOrder,
  onClick,
  tooltip,
  meshRef,
}: BoundingMaskMeshProps): JSX.Element {
  const maxTextureSize = useThree((s) =>
    Math.min(s.gl.capabilities.maxTextureSize, MAX_TEXTURE_SIZE),
  );

  const { mesh, crop } = useMemo(() => {
    let crop = crops.get(mask);
    if (!crop) {
      crop = rasterizeRleCrop(mask, mask.boundingBox, maxTextureSize);
      crops.set(mask, crop);
    }
    const texture = new DataTexture(
      toAlphaMap(crop),
      crop.width,
      crop.height,
      RGFormat,
    );
    texture.magFilter = LinearFilter;
    texture.minFilter = LinearFilter;
    texture.needsUpdate = true;

    const mesh = new Mesh(
      boundingBoxToSphereGeometry(maskBoxCoordinates(mask, imageSize)),
      new MeshBasicMaterial({
        color,
        alphaMap: texture,
        side: DoubleSide,
        transparent: true,
        opacity: FILL_OPACITY,
        depthTest: false,
        depthWrite: false,
      }),
    );
    mesh.renderOrder = renderOrder;
    return { mesh, crop };
  }, [mask, imageSize, color, renderOrder, maxTextureSize]);

  useLotvDispose(mesh);
  // The disposal of the mesh leaves the textures of its material
  useEffect(() => () => mesh.material.alphaMap?.dispose(), [mesh]);

  // The point under the pointer, in the local space of the mesh's parent, while the pointer is on the mask.
  const [hoverPoint, setHoverPoint] = useState<Vector3>();
  const isHovered = !!hoverPoint;
  useEffect(() => {
    mesh.material.opacity = isHovered ? HOVERED_OPACITY : FILL_OPACITY;
  }, [mesh, isHovered]);
  useCursor(isHovered && !!onClick);

  const canvas = useThree((s) => s.gl.domElement);
  useClickBeforeCanvas(canvas, isHovered ? onClick : undefined);

  // Other findings lie on the same sphere, so a miss lets the event through to them.
  function onPointerMove(event: ThreeEvent<PointerEvent>): void {
    const isOnMask = !!event.uv && isCovered(crop, event.uv);
    setHoverPoint(
      isOnMask ? mesh.parent?.worldToLocal(event.point.clone()) : undefined,
    );
    if (isOnMask) event.stopPropagation();
  }

  return (
    <>
      <primitive
        object={mesh}
        ref={meshRef}
        onPointerMove={onPointerMove}
        onPointerOut={() => setHoverPoint(undefined)}
      />
      {tooltip && hoverPoint && (
        <AppAwareHtml position={hoverPoint} style={TOOLTIP_STYLE}>
          <FaroText
            dark
            variant="bodyM"
            className={NO_TRANSLATE_CLASS}
            sx={{
              display: "block",
              py: 0.75,
              px: 1.5,
              backgroundColor: grey[1000],
              outline: `1px solid ${alpha(color, 0.5)}`,
              borderRadius: 1.5,
              // As wide as the text, up to a few words per line
              width: "max-content",
              maxWidth: "260px",
              whiteSpace: "normal",
              overflowWrap: "break-word",
            }}
          >
            {tooltip}
          </FaroText>
        </AppAwareHtml>
      )}
    </>
  );
}

/**
 * Handle clicks on the canvas before anything in it does, like the HTML labels of the findings, which the canvas never
 * gets the clicks of. Otherwise the scene would react to them too, and before the mask:
 * - the panorama and the waypoints handle `click`, which is stopped here;
 * - walk mode jumps to the waypoint closest to the cursor on `pointerup`. That can't be stopped, as the camera controls
 *   need it to end a drag, so it is marked with `preventDefault()` instead, and walk mode skips marked events.
 * Pressing and moving still reach the canvas, so the view can be dragged as usual.
 *
 * @param canvas The canvas of the scene.
 * @param onClick Called for a click on the canvas; undefined to leave the clicks to the canvas.
 */
function useClickBeforeCanvas(
  canvas: HTMLCanvasElement,
  onClick: (() => void) | undefined,
): void {
  useEffect(() => {
    if (!onClick) return;
    let pressedAt: { x: number; y: number } | undefined;
    // A release after dragging the view, or after pressing elsewhere, is not a click.
    function isClick(event: MouseEvent): boolean {
      return (
        event.target === canvas &&
        !!pressedAt &&
        Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y) <=
          CLICK_TOLERANCE
      );
    }
    function onPointerDown(event: PointerEvent): void {
      pressedAt = { x: event.clientX, y: event.clientY };
    }
    function onPointerUp(event: PointerEvent): void {
      if (isClick(event)) event.preventDefault();
    }
    function onCanvasClick(event: MouseEvent): void {
      if (event.target !== canvas) return;
      event.stopImmediatePropagation();
      if (isClick(event)) onClick?.();
    }
    // On the window, in the capture phase, so they run before the canvas' own listeners.
    const capture = { capture: true };
    window.addEventListener("pointerdown", onPointerDown, capture);
    window.addEventListener("pointerup", onPointerUp, capture);
    window.addEventListener("click", onCanvasClick, capture);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, capture);
      window.removeEventListener("pointerup", onPointerUp, capture);
      window.removeEventListener("click", onCanvasClick, capture);
    };
  }, [canvas, onClick]);
}

/** Next to the cursor, and never in the way of the pointer */
const TOOLTIP_STYLE = {
  pointerEvents: "none",
  transform: "translate(16px, 16px)",
} as const;

/**
 * @param crop The coverage bitmap of a mask, top row first.
 * @param uv A texture coordinate on the mask's mesh, with v = 0 at the bottom of the box.
 * @returns Whether the mask covers the texel at the coordinate.
 */
function isCovered(crop: MaskCrop, uv: Vector2): boolean {
  const x = Math.min(Math.floor(uv.x * crop.width), crop.width - 1);
  const row = Math.min(Math.floor((1 - uv.y) * crop.height), crop.height - 1);
  return crop.data[row * crop.width + x] > 0;
}

/**
 * Turn a coverage bitmap into the texels of an alpha map. three.js reads an alpha map's green channel, so the
 * coverage goes there, in a two-channel texture. The bitmap's top row is the top of the box, while a DataTexture's
 * first row is at v = 0, which the sphere geometry puts at the bottom of the box, so the rows are flipped.
 *
 * @param crop The coverage bitmap, top row first.
 * @returns The red-green texels, bottom row first, with the coverage as green.
 */
function toAlphaMap({ data, width, height }: MaskCrop): Uint8Array {
  const texels = new Uint8Array(width * height * 2);
  for (let row = 0; row < height; row++) {
    const target = (height - 1 - row) * width;
    for (let x = 0; x < width; x++) {
      texels[(target + x) * 2 + 1] = data[row * width + x];
    }
  }
  return texels;
}
